import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { textColorMixin, textStyleMixin } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getInvertedColor,
  roundTo4
} from '@elfsight/widget-utils';
import { commonFieldsSettings } from '../commonFieldSettings';
import { LINE_HEIGHT_RATIO } from '../constants';
import type { CommonFieldProps } from '../types';

export interface NumberScaleProps extends CommonFieldProps<number> {
  range: number[];
  rightCaption?: string;
  leftCaption?: string;
  fieldActiveColor?: string;
  fieldHintColor?: string;
  fieldHintFontStyle?: TextStyleValue;
}

export function NumberScale({
  id,
  label,
  range,
  rightCaption,
  leftCaption,
  onChange,
  onFocus,
  value,
  required,
  fieldColor = commonFieldsSettings.fieldColor,
  fieldFillColor = commonFieldsSettings.fieldFillColor,
  fieldActiveColor = commonFieldsSettings.fieldActiveColor,
  fieldBorderType = commonFieldsSettings.fieldBorderType,
  fieldBorderColor = commonFieldsSettings.fieldBorderColor,
  fieldBorderWidth = commonFieldsSettings.fieldBorderWidth,
  fieldBorderRadius = commonFieldsSettings.fieldBorderRadius,
  fieldHintColor = commonFieldsSettings.fieldHintColor,
  fieldFontStyle = commonFieldsSettings.fieldFontStyle,
  fieldHintFontStyle = commonFieldsSettings.fieldHintFontStyle
}: NumberScaleProps) {
  const [checkedIndex, setCheckedIndex] = useState(-1);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const { fontSize: fieldHintFontSize } = fieldHintFontStyle;

  const handleMouseOver = (i: number) => setHoverIndex(i);
  const handleCheck = useCallback(
    (event: ChangeEvent<HTMLInputElement>, i: number) => {
      const { value } = event.target;
      onChange?.(parseInt(value), event);
      setCheckedIndex(i);
    },
    [onChange]
  );

  useEffect(() => {
    if (value !== undefined) {
      setCheckedIndex(Number(value));
    }
  }, [value]);

  const [minNumber, maxNumber] = range;
  const numberWidth = `calc((100% + 4px) / ${maxNumber - minNumber + 1} - 4px)`;
  const borderColor = fieldBorderType ? fieldBorderColor : 'transparent';
  const withUnderline = fieldBorderType === 'underline';
  const maxBorderRadius = Math.round((fieldFontStyle.fontSize * 7) / 6);
  const borderRadius = Math.min(fieldBorderRadius, maxBorderRadius);

  return (
    <Container>
      <Numbers role="radiogroup" aria-required={required}>
        {Array.from({ length: maxNumber - minNumber + 1 }, (_, index) => {
          const derivedIndex = minNumber + index;

          return (
            <NumberContainer
              key={`number-scale-${id}${derivedIndex}`}
              onMouseEnter={() => handleMouseOver(derivedIndex)}
              onMouseLeave={() => handleMouseOver(-1)}
              _width={numberWidth}
              _activeColor={fieldActiveColor}
              _fillColor={fieldFillColor}
              _fieldColor={fieldColor}
              _borderColor={borderColor}
              _borderRadius={borderRadius}
              _borderWidth={fieldBorderWidth}
              _withUnderline={withUnderline}
              _index={derivedIndex}
              _checkedIndex={checkedIndex}
              _hoverIndex={hoverIndex}
            >
              <Input
                type="radio"
                name={label}
                value={derivedIndex}
                onChange={(e) => handleCheck(e, derivedIndex)}
                onFocus={onFocus}
              />
              <NumberLabel _textStyle={fieldFontStyle}>
                {derivedIndex}
              </NumberLabel>
            </NumberContainer>
          );
        })}
      </Numbers>
      <LeftCaption _textColor={fieldHintColor} _size={fieldHintFontSize}>
        {leftCaption}
      </LeftCaption>
      <RightCaption _textColor={fieldHintColor} _size={fieldHintFontSize}>
        {rightCaption}
      </RightCaption>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-container'
  })
})`
  display: inline-block;
  width: 100%;
`;

const Numbers = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-numbers'
  })
})`
  margin: 0 auto;
  width: 100%;
  display: flex;
`;

interface NumberContainerProps {
  _width: string;
  _activeColor: string;
  _fillColor: string;
  _fieldColor: string;
  _borderColor: string;
  _borderRadius: number;
  _borderWidth: number;
  _withUnderline: boolean;
  _index: number;
  _checkedIndex: number;
  _hoverIndex: number;
}

const NumberContainer = styled.label.attrs<NumberContainerProps>({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-number-container'
  })
})`
  position: relative;
  transition: 0.1s all ease;
  width: ${({ _width }) => _width};
  color: ${({ _index, _checkedIndex, _activeColor, _fieldColor }) =>
    _checkedIndex === _index ? getInvertedColor(_activeColor) : _fieldColor};
  background: ${({ _index, _checkedIndex, _activeColor, _fillColor }) =>
    _checkedIndex === _index ? _activeColor : _fillColor};
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
  ${({ _withUnderline, _borderWidth }) =>
    _withUnderline
      ? css`
          border: none;
          border-bottom: ${_borderWidth}px solid;
        `
      : css`
          border: ${_borderWidth}px solid;
        `}
  border-color: ${({
    _index,
    _hoverIndex,
    _checkedIndex,
    _activeColor,
    _borderColor
  }) =>
    _hoverIndex === _index || _checkedIndex === _index
      ? _activeColor
      : _borderColor};

  &:not(:last-child) {
    margin-right: 4px;
  }
`;

const Input = styled.input.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-input'
  })
})`
  position: absolute;
  cursor: pointer;
  margin: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
`;

interface NumberLabelProps {
  _textStyle: TextStyleValue;
}

const NumberLabel = styled.div.attrs<NumberLabelProps>({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-label'
  })
})`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: ${({ _textStyle }) =>
    roundTo4(_textStyle.fontSize * LINE_HEIGHT_RATIO)}px;
  padding: ${({ _textStyle }) => _textStyle.fontSize}px 0;
  ${textStyleMixin};
`;

interface CaptionProps {
  _textColor: string;
  _size: number;
}

const Caption = styled.div.attrs<CaptionProps>({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-caption'
  })
})`
  margin-top: 4px;
  ${textColorMixin}
  font-size: ${({ _size }) => _size}px;
  line-height: ${({ _size }) => roundTo4(_size * LINE_HEIGHT_RATIO)}px;
`;

const LeftCaption = styled(Caption).attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-left-caption'
  })
})`
  float: left;
`;

const RightCaption = styled(Caption).attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'number-scale-right-caption'
  })
})`
  float: right;
`;
